/**
 * GPU frame time through `EXT_disjoint_timer_query_webgl2` where the browser
 * has it (after the dungeon-floors prototype's `profiling.js`), else nothing:
 * `last()` stays null. One query per rendered frame; results arrive a frame
 * or more later and are collected on the next `begin`. Diagnostics only: no
 * decision depends on them.
 */
type TimerExt = { readonly TIME_ELAPSED_EXT: number; readonly GPU_DISJOINT_EXT: number };

const isTimerExt = (v: unknown): v is TimerExt =>
  typeof v === 'object' && v !== null && typeof (v as Partial<TimerExt>).TIME_ELAPSED_EXT === 'number' && typeof (v as Partial<TimerExt>).GPU_DISJOINT_EXT === 'number';

/** Samples kept for `samples()`. */
const keep = 1024;

export type GpuTimer = {
  begin(): void;
  end(): void;
  last(): number | null;
  samples(): readonly number[];
  /** How many frames have been measured since mount (the samples keep only the last 1024). */
  measured(): number;
  dispose(): void;
};

/** `carry` keeps the samples of a timer this one replaces (a rebuild after a lost context). */
export const createGpuTimer = (gl: WebGL2RenderingContext | WebGLRenderingContext, carry?: GpuTimer): GpuTimer => {
  const ext: unknown = gl.getExtension('EXT_disjoint_timer_query_webgl2');
  const times: number[] = carry?.samples().slice() ?? [];
  let measured = carry?.measured() ?? 0;
  if (!(gl instanceof WebGL2RenderingContext) || !isTimerExt(ext)) {
    return { begin: () => undefined, end: () => undefined, last: () => times.at(-1) ?? null, samples: () => [...times], measured: () => measured, dispose: () => undefined };
  }
  const pending: WebGLQuery[] = [];
  let active: WebGLQuery | null = null;
  const collect = (): void => {
    while (pending.length > 0) {
      const q = pending[0];
      if (q === undefined || !(gl.getQueryParameter(q, gl.QUERY_RESULT_AVAILABLE) as boolean)) break;
      pending.shift();
      const disjoint = gl.getParameter(ext.GPU_DISJOINT_EXT) as boolean;
      if (!disjoint) {
        times.push((gl.getQueryParameter(q, gl.QUERY_RESULT) as number) / 1e6);
        measured += 1;
      }
      if (times.length > keep) times.shift();
      gl.deleteQuery(q);
    }
  };
  return {
    begin() {
      collect();
      if (active !== null) return;
      active = gl.createQuery();
      if (active !== null) gl.beginQuery(ext.TIME_ELAPSED_EXT, active);
    },
    end() {
      if (active === null) return;
      gl.endQuery(ext.TIME_ELAPSED_EXT);
      pending.push(active);
      active = null;
    },
    last: () => {
      collect();
      return times.at(-1) ?? null;
    },
    samples: () => {
      collect();
      return [...times];
    },
    measured: () => {
      collect();
      return measured;
    },
    dispose() {
      for (const q of pending.splice(0)) gl.deleteQuery(q);
      if (active !== null) gl.deleteQuery(active);
      active = null;
    },
  };
};
